import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Phone, ArrowLeft } from "lucide-react";
import { BRAND } from "@/lib/brand";
import useSeo from "@/lib/useSeo";

export default function TermsPage() {
  useSeo({
    title: "Terms & Conditions — Top-Notch Pro Solutions",
    description: "Terms and Conditions for Top-Notch Pro Solutions. Last updated June 18, 2026.",
  });
  return (
    <div data-testid="terms-page" className="max-w-3xl mx-auto px-6 lg:px-8 py-12 md:py-20">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[var(--brand-teal)] mb-6" data-testid="terms-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-[var(--brand-teal)] text-white flex items-center justify-center">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <span className="section-eyebrow">Legal</span>
      </div>
      <h1 className="heading-display text-4xl md:text-5xl">Terms and Conditions</h1>
      <p className="mt-3 text-sm text-slate-500"><strong>Last Updated:</strong> June 18, 2026</p>

      <div className="mt-10 space-y-8 text-slate-700 leading-relaxed">
        <p>
          Welcome to <strong>{BRAND.legalName}</strong> ("Company," "we," "us," or "our"). By accessing or using our
          website, you agree to comply with and be bound by the following Terms and Conditions. If you do not agree to
          these terms, please do not use our website or services.
        </p>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>1. Age Requirement</h2>
          <p>
            By using this website and requesting our services, you affirm that you are at least 18 years of age or
            older. We do not knowingly provide services to or collect data from individuals under the age of 18.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>2. Services Offered</h2>
          <p>
            {BRAND.legalName} provides professional cleaning, handyman & repairs, furniture assembly, fixture
            installation, light home improvement, and property management services.
          </p>
          <ul className="mt-3 space-y-2 list-disc pl-5">
            <li><strong>Instant Booking:</strong> Cleaning services may be eligible for instant booking as indicated on the website.</li>
            <li><strong>Quote Requests:</strong> For all other services (including handyman, assembly, and property management), users may submit an online request form to receive a project estimate. Estimates are subject to change based on job scope revisions.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>3. User Conduct & Submissions</h2>
          <p>
            When using our contact or quote request forms, you agree to provide accurate, current, and complete
            information. You may not upload photos or content that are unlawful, obscene, defamatory, or violate the
            intellectual property rights of others.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>4. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, {BRAND.legalName} shall not be liable for any indirect,
            incidental, or consequential damages arising out of your use of our website or the services booked
            through it.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>5. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms and Conditions at any time. Any changes will be posted on this
            page with an updated "Last Updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>6. Contact Us</h2>
          <p>If you have any questions regarding these Terms, please contact us at:</p>
          <div className="mt-4 rounded-xl bg-[var(--brand-cream-soft)] p-5 space-y-2">
            <a href="mailto:topnotchprosolutions@gmail.com" className="flex items-center gap-2 text-[var(--brand-teal)] font-semibold hover:underline">
              <Mail className="w-4 h-4 text-[var(--brand-gold)]" /> topnotchprosolutions@gmail.com
            </a>
            <a href={BRAND.phoneHref} className="flex items-center gap-2 text-[var(--brand-teal)] font-semibold hover:underline">
              <Phone className="w-4 h-4 text-[var(--brand-gold)]" /> {BRAND.phone}
            </a>
          </div>
        </section>
      </div>

      <div className="mt-12 text-center text-xs text-slate-500">
        <Link to="/privacy" className="text-[var(--brand-teal)] hover:underline">View our Privacy Policy →</Link>
      </div>
    </div>
  );
}
